import { lazy, Suspense, useId } from "react";

import { cn } from "@/lib/utils";

// Lazy-load the heavy MD editor (CodeMirror etc.) so it doesn't block initial paint.
const MDEditor = lazy(() => import("@uiw/react-md-editor").then((m) => ({ default: m.default })));

export type MarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  ariaLabel?: string;
  className?: string;
  preview?: "edit" | "live" | "preview";
};

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  height = 240,
  ariaLabel,
  className,
  preview = "edit",
}: MarkdownEditorProps) {
  const id = useId();
  return (
    <div
      data-color-mode="light"
      className={cn("rounded-md border bg-background", className)}
      role="group"
      aria-labelledby={ariaLabel ? `${id}-label` : undefined}
      aria-label={ariaLabel}
    >
      {ariaLabel ? (
        <span id={`${id}-label`} className="sr-only">
          {ariaLabel}
        </span>
      ) : null}
      <Suspense fallback={<div className="h-48 animate-pulse rounded-md border bg-muted/40" />}>
        <MDEditor
          value={value}
          onChange={(v) => onChange(v ?? "")}
          height={height}
          preview={preview}
          textareaProps={{
            placeholder,
            spellCheck: false,
          }}
          hideToolbar={false}
        />
      </Suspense>
    </div>
  );
}
